import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import {
  completeMembershipPayment,
  completeOfflineRegistration,
  completeProgramFeePayment,
  quotePayment,
} from '../actions/programRegistrationActions'
import {
  MEMBERSHIP_DUE_ACTIONS,
  resetPaymentSession,
  selectElementsSetup,
  setBillingAddressError,
  setIsProcessingPayment,
  setPaymentError,
} from '../reducers/programRegistrationReducer'
import { getConnectedAccountStripe } from '../utility/stripe'

const REQUOTE_ERRORS = ['quoteRequired', 'membershipNotPaid', 'membershipSeatsExhausted', 'membershipRequired', 'feeNotSecured', 'cardDeclined']
const RESTART_ERRORS = ['notPayable', 'courseChecksFailed']

const useCompletePaymentSession = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const stripe = useStripe()
  const elements = useElements()
  const currentUser = useSelector(state => state.user.current_user)
  const paymentSession = useSelector(state => state.programRegistration.paymentSession)
  const billingAddressError = useSelector(state => state.programRegistration.billingAddressError)
  const elementsSetup = useSelector(selectElementsSetup)
  const quote = paymentSession?.quote
  const programFeeByCard = !!quote?.cardRequired &&
    quote.pricing.lines.some(line => line.key === 'program_fee' && line.paymentMethod === 'card')
  const membershipDue = MEMBERSHIP_DUE_ACTIONS.includes(quote?.membership?.action)
  // a membership purchase or upgrade mounts the form on the platform; everything else stays on the club
  const onPlatform = !!elementsSetup && !elementsSetup.stripeAccountId
  const billingDetails = { email: currentUser?.email }

  const showError = (message) => {
    dispatch(setPaymentError(message || t('programs.registration.checkoutStep.paymentFailed')))
  }

  const requote = () => {
    dispatch(quotePayment({ registrationId: paymentSession.registrationId, ...quote.params }))
  }

  const askServer = async (completion, args) => {
    try {
      return await dispatch(completion({ registrationId: paymentSession.registrationId, ...args })).unwrap()
    } catch (rejection) {
      if (rejection?.invalidBillingAddress) {
        dispatch(setBillingAddressError(rejection.invalidBillingAddress[0]))
        return null
      }

      const requoteError = REQUOTE_ERRORS.find(key => rejection?.[key])

      if (requoteError) {
        showError(rejection[requoteError][0])
        requote()
        return null
      }

      const restartError = RESTART_ERRORS.find(key => rejection?.[key])

      if (restartError) {
        showError(rejection[restartError][0])
        dispatch(resetPaymentSession())
        return null
      }

      showError()
      return null
    }
  }

  const submitForm = async () => {
    const { error } = await elements.submit()

    if (error) {
      showError(error.message)
      return false
    }

    return true
  }

  const cardRefused = (error) => {
    showError(error.message)
    requote()
    return false
  }

  const confirmWithForm = async ({ paymentIntentClientSecret }) => {
    const { error } = await stripe.confirmPayment({
      elements,
      clientSecret: paymentIntentClientSecret,
      confirmParams: { return_url: window.location.href, payment_method_data: { billing_details: billingDetails } },
      redirect: 'if_required',
    })

    return error ? cardRefused(error) : true
  }

  const confirmOnClub = async ({ paymentIntentClientSecret, stripeAccountId }, clonedPaymentMethodId) => {
    const clubStripe = await getConnectedAccountStripe(stripeAccountId)
    const { error } = await clubStripe.confirmCardPayment(paymentIntentClientSecret, { payment_method: clonedPaymentMethodId })

    return error ? cardRefused(error) : true
  }

  const confirmOnPlatform = async ({ paymentIntentClientSecret }, paymentMethodId) => {
    const { error } = await stripe.confirmCardPayment(paymentIntentClientSecret, { payment_method: paymentMethodId })

    return error ? cardRefused(error) : true
  }

  const createCard = async () => {
    const { paymentMethod, error } = await stripe.createPaymentMethod({ elements, params: { billing_details: billingDetails } })

    return error ? cardRefused(error) : paymentMethod.id
  }

  const holdProgramFee = async (paymentMethodId) => {
    const programFee = await askServer(completeProgramFeePayment, { paymentMethodId })

    if (!programFee) {
      return false
    }

    if (programFee.status === 'succeeded') {
      return true
    }

    const { paymentMethodId: clonedPaymentMethodId } = programFee

    return clonedPaymentMethodId ? confirmOnClub(programFee, clonedPaymentMethodId) : confirmWithForm(programFee)
  }

  const payMembership = async (paymentMethodId) => {
    const membership = await askServer(completeMembershipPayment, { paymentMethodId })

    if (!membership) {
      return false
    }

    if (membership.status === 'succeeded') {
      return true
    }

    return confirmOnPlatform(membership, paymentMethodId)
  }

  return async () => {
    const sessionReady = paymentSession?.status === 'ready' && quote?.status === 'ready'
    const formReady = !quote?.cardRequired || (stripe && elements)

    if (!sessionReady || !formReady || billingAddressError) {
      return null
    }

    const { registrationId } = paymentSession

    dispatch(setIsProcessingPayment(true))
    dispatch(setPaymentError(null))

    try {
      if (!quote.cardRequired) {
        const offline = await askServer(completeOfflineRegistration)
        return offline ? registrationId : null
      }

      if (!await submitForm()) {
        return null
      }

      let paymentMethodId = null

      if (onPlatform) {
        paymentMethodId = await createCard()

        if (!paymentMethodId) {
          return null
        }
      }

      if (programFeeByCard && !await holdProgramFee(paymentMethodId)) {
        return null
      }

      if (membershipDue && !await payMembership(paymentMethodId)) {
        return null
      }

      return registrationId
    } finally {
      dispatch(setIsProcessingPayment(false))
    }
  }
}

export default useCompletePaymentSession
